import React from "react";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../store/Store";
import { useTariffPoll } from "./useTariffPoll";
import { IDestinationExtension, IQuoteExtension } from "../../../types/IQuotes";
import { IMultiLocationRoute } from "../../../types/IMultiLocationRoute";
import { convertQuotesToRoutes } from "../../../utils/convertQuotesToRoutes";

const routeKey = (route: IMultiLocationRoute) =>
  `${route.origin.id}-${route.destination.id}-${route.mode}-${route.competitor}`;

/**
 * Stable host for tariff polling that survives modal unmounts.
 *
 * Other components (e.g. RailRateRequestPanel) enqueue requests via
 * `enqueueTariffPollRequests`. This component drains the queue and calls
 * `startTariffPoll` from a lifecycle that is never torn down mid-poll.
 *
 * Mount once from a page-level component that stays alive for the full
 * desktop multi-location quote session (e.g. HomePage).
 */
export const TariffPollRunner: React.FC = () => {
  const [pendingTariffPollRequests, clearPendingTariffPollRequests] =
    useNitroCAStore((state) => [
      state.pendingTariffPollRequests,
      state.clearPendingTariffPollRequests,
    ]);

  const setMultiLocationDestinations = useNitroStore(
    (state) => state.setMultiLocationDestinations,
  );

  const moveToOriginTable = React.useCallback(
    (params: {
      rowKey: string;
      quote: IQuoteExtension;
      foundQuote: IQuoteExtension;
      destination: IDestinationExtension;
    }) => {
      const fn =
        useNitroCAStore.getState().moveToOriginTableRegistry[
          params.destination.id
        ];
      fn?.({ quote: params.quote, foundQuote: params.foundQuote });

      // Drop the found rate straight onto the map from data we already have — no refetch needed.
      const [route] = convertQuotesToRoutes([
        {
          destination: params.destination,
          quotes: [params.foundQuote as IQuoteExtension],
        },
      ]);
      if (!route) return;

      const current = useNitroStore.getState().multiLocationDestinations;
      setMultiLocationDestinations([
        ...current.filter((r) => routeKey(r) !== routeKey(route)),
        route,
      ]);
    },
    [setMultiLocationDestinations],
  );

  const { startTariffPoll } = useTariffPoll({ moveToOriginTable });

  React.useEffect(() => {
    if (pendingTariffPollRequests.length === 0) return;

    // startTariffPoll's own store guard blocks duplicates while a poll is active.
    pendingTariffPollRequests.forEach((req) => {
      startTariffPoll(req.rowKey, req.quote, req.destination, {
        silent: req.silent,
      });
    });

    clearPendingTariffPollRequests();
  }, [
    pendingTariffPollRequests,
    startTariffPoll,
    clearPendingTariffPollRequests,
  ]);

  // Nothing to render — this is a behaviour-only component.
  return null;
};
